import { getStudentEnrollmentDetails } from '@/features/scheduling/utils/getStudentEnrollmentDetails';
import type { Student } from '@/types/student';
import { TZDate } from '@date-fns/tz';
import { startOfMonth } from 'date-fns';
import type { StudentSchedulingDetails } from '../../../api/getStudentsSchedulingDetails';
import type { StudentsSessionDetails } from '../../ScheduleDialog/context/ScheduleFlowContext';

/**
 * The month the calendar should open on when an enrollment has not started yet (MYM-398).
 *
 * With several students the latest start wins: it is the first month all of them can be booked,
 * which the shared time slot requires.
 *
 * Returns undefined when no selected enrollment starts in a future month, leaving the current
 * month as the default.
 */
export const getInitialDisplayMonth = (
  students: Student[],
  studentsConfig: StudentSchedulingDetails[],
  studentSessionDetails: StudentsSessionDetails[],
  today: Date,
  timeZone: string,
): Date | undefined => {
  const currentMonth = startOfMonth(today);

  const startMonths = students.flatMap((student) => {
    const enrollmentId = studentSessionDetails.find((detail) => detail.studentId === student.id)?.enrollmentId;
    if (!enrollmentId) return [];

    const startDate = getStudentEnrollmentDetails(studentsConfig, student.id, enrollmentId)?.enrollmentStartDate;
    if (!startDate) return [];

    // The API sends the start date as a UTC-midnight marker, so its month has to be read in UTC:
    // west of UTC, local time lands on the previous day and would pick the previous month.
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return [];

    const startMonth = new TZDate(start.getUTCFullYear(), start.getUTCMonth(), 1, timeZone);
    return startMonth > currentMonth ? [startMonth] : [];
  });

  if (startMonths.length === 0) return undefined;

  return new TZDate(Math.max(...startMonths.map((month) => month.getTime())), timeZone);
};
